import React, { useEffect, useState } from "react";
import {
  Col,
  Row,
  Input,
  Button,
  Layout,
  Popover,
  Select,
  Table,
  Skeleton,
  Pagination,
  Typography,
  message,
  Card,
} from "antd";
import dayjs from "dayjs";
import { FaSearch, FaFilter, FaEye, FaEdit, FaArrowLeft } from "react-icons/fa";
import { Get } from "../../config/api/get";
import { ASSETS } from "../../config/constants/api";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import "../upcoming-lessons/upcoming-lessons.css";
import "../course-management/course-management.css";
import "../quiz-management/quiz-management.css";
import "../quiz-management/quiz-details.css";
import "./asset-management.css";

const ASSET_TYPES = [
  { value: "IMAGE", label: "IMAGE" },
  { value: "VIDEO", label: "VIDEO" },
  { value: "PDF", label: "PDF" },
];

const STATUS_OPTIONS = [
  { value: "ACTIVE", label: "ACTIVE" },
  { value: "INACTIVE", label: "INACTIVE" },
];

function AssetManagement() {
  const token = useSelector((state) => state.user.userToken);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);
  const [assets, setAssets] = useState([]);
  const [paginationConfig, setPaginationConfig] = useState({
    pageNumber: 1,
    limit: 10,
    totalDocs: 0,
    totalPages: 0,
  });
  const [filter, setFilter] = useState({
    keyword: "",
    type: undefined,
    status: undefined,
  });

  const startIndex = (paginationConfig.pageNumber - 1) * paginationConfig.limit + 1;
  const endIndex = Math.min(
    startIndex + paginationConfig.limit - 1,
    paginationConfig.totalDocs
  );
  const messageRecord = `Showing records ${endIndex} of ${paginationConfig.totalDocs}`;

  const fetchAssets = async (page, limit, keyword, type, status) => {
    setLoading(true);
    try {
      const queryObj = {
        page: String(page || paginationConfig.pageNumber),
        limit: String(limit || paginationConfig.limit),
      };
      if (keyword) queryObj.keyword = keyword;
      if (type) queryObj.type = type;
      if (status) queryObj.status = status;

      const res = await Get(ASSETS.getAssets, token, queryObj);
      const data = res?.data ?? res;
      if (data?.docs) {
        setAssets(data.docs);
        setPaginationConfig({
          pageNumber: data.page ?? page ?? 1,
          limit: data.limit ?? limit ?? 10,
          totalDocs: data.totalDocs ?? 0,
          totalPages: data.totalPages ?? 0,
        });
      } else {
        message.error(data?.message || "Failed to load assets");
      }
    } catch (e) {
      message.error(e?.message || "Failed to load assets");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAssets(
      paginationConfig.pageNumber,
      paginationConfig.limit,
      filter.keyword || undefined,
      filter.type,
      filter.status
    );
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handlePageChange = (page) => {
    setPaginationConfig((prev) => ({ ...prev, pageNumber: page }));
    fetchAssets(
      page,
      paginationConfig.limit,
      filter.keyword || undefined,
      filter.type,
      filter.status
    );
  };

  const handleLimitChange = (limit) => {
    setPaginationConfig((prev) => ({ ...prev, limit, pageNumber: 1 }));
    fetchAssets(1, limit, filter.keyword || undefined, filter.type, filter.status);
  };

  const handleSearch = () => {
    setPaginationConfig((prev) => ({ ...prev, pageNumber: 1 }));
    fetchAssets(
      1,
      paginationConfig.limit,
      filter.keyword || undefined,
      filter.type,
      filter.status
    );
  };

  const handleFilterApply = () => {
    setFilterOpen(false);
    setPaginationConfig((prev) => ({ ...prev, pageNumber: 1 }));
    fetchAssets(
      1,
      paginationConfig.limit,
      filter.keyword || undefined,
      filter.type,
      filter.status
    );
  };

  const resetFilter = () => {
    setFilter({ keyword: "", type: undefined, status: undefined });
    setFilterOpen(false);
    setPaginationConfig((prev) => ({ ...prev, pageNumber: 1 }));
    fetchAssets(1, paginationConfig.limit);
  };

  const columns = [
    {
      title: "#",
      key: "index",
      width: 70,
      render: (_, __, index) => (
        <span className="upcoming-lessons-cell-id">
          {(paginationConfig.pageNumber - 1) * paginationConfig.limit + index + 1}
        </span>
      ),
    },
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
      ellipsis: true,
      render: (title) => (
        <span className="upcoming-lessons-cell-name" title={title}>
          {title}
        </span>
      ),
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      width: 100,
      render: (type) => (
        <span className="upcoming-lessons-cell-name">{type || "—"}</span>
      ),
    },
    {
      title: "Courses",
      key: "courses",
      ellipsis: true,
      render: (_, record) => {
        const courses = record?.courses || [];
        if (!Array.isArray(courses) || courses.length === 0) {
          return <span className="upcoming-lessons-cell-name">—</span>;
        }
        const text =
          courses.map((c) => (typeof c === "object" ? c?.title : c)).filter(Boolean).join(", ") ||
          "—";
        return (
          <span className="upcoming-lessons-cell-name" title={text}>
            {text}
          </span>
        );
      },
    },

    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 100,
      render: (status) => (
        <span className="upcoming-lessons-cell-name" style={{ textTransform: "uppercase" }}>
          {status ?? "—"}
        </span>
      ),
    },
    {
      title: "Created At",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 120,
      render: (date) => (
        <span className="upcoming-lessons-cell-date">
          {date ? dayjs(date).format("M/D/YYYY") : "—"}
        </span>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 120,
      fixed: "right",
      render: (_, record) => (
        <div className="quiz-management-action-cell">
          <button
            type="button"
            className="quiz-mgmt-icon-btn"
            aria-label="View asset"
            onClick={() =>
              navigate(`/asset-management/${record._id}`, {
                state: { asset: record },
              })
            }
          >
            <FaEye style={{ fontSize: 16 }} />
          </button>
          <button
            type="button"
            className="quiz-mgmt-icon-btn"
            aria-label="Edit asset"
            onClick={() =>
              navigate(`/asset-management/${record._id}/edit`, {
                state: { asset: record },
              })
            }
          >
            <FaEdit style={{ fontSize: 16 }} />
          </button>
        </div>
      ),
    },
  ];

  const filterContent = (
    <div className="filterDropdown upcoming-lessons-filter-popover">
      <p className="upcoming-lessons-filter-head">Filter assets</p>
      <hr />
      <div className="filterDropdownBody">
        <p className="mainLabel">Type</p>
        <Select
          size="large"
          className="filterSelectBox upcoming-lessons-filter-select"
          placeholder="Select Type"
          value={filter.type}
          onChange={(v) => setFilter((f) => ({ ...f, type: v }))}
          allowClear
          options={ASSET_TYPES}
        />
        <p className="mainLabel">Status</p>
        <Select
          size="large"
          className="filterSelectBox upcoming-lessons-filter-select"
          placeholder="Select Status"
          value={filter.status}
          onChange={(v) => setFilter((f) => ({ ...f, status: v }))}
          allowClear
          options={STATUS_OPTIONS}
        />
        <Button
          type="primary"
          shape="round"
          block
          size="large"
          className="mainButton primaryButton loginButton upcoming-lessons-filter-apply"
          onClick={handleFilterApply}
        >
          Apply
        </Button>
        <Button
          type="primary"
          shape="round"
          block
          size="large"
          className="mainButton primaryButton2 loginButton"
          onClick={resetFilter}
        >
          Clear All
        </Button>
      </div>
    </div>
  );

  const itemRender = (_, type, originalElement) => {
    if (type === "prev") {
      return (
        <button type="button" className="upcoming-lessons-pager-btn">
          Previous
        </button>
      );
    }
    if (type === "next") {
      return (
        <button type="button" className="upcoming-lessons-pager-btn">
          Next
        </button>
      );
    }
    return originalElement;
  };

  return (
    <Layout
      className="configuration upcoming-lessons-page quiz-details-page"
      style={{ minHeight: "100vh" }}
    >
      <Row className="blueBackground-old coursebnr asset-management-hero">
        <Typography.Title
          level={1}
          className="fontFamily1 aboutbanner-content upcoming-lessons-hero-title"
        >
          Resource Library
        </Typography.Title>
      </Row>

      <div className="boxDetails">
        <Row
          className="whiteBackground upcoming-lessons-section"
          style={{ justifyContent: "center" }}
        >
          <Col xs={24} lg={22} xl={21}>
            <div className="upcoming-lessons-inner">
              <Card className="infoBox upcoming-lessons-card" bordered={false}>
                <div className="upcoming-lessons-box">
                  <Row className="upcoming-lessons-toolbar" gutter={[16, 16]}>
                    <Col xs={24} md={14} lg={16}>
                      <div className="course-management-title-block">
                        <button
                          type="button"
                          className="course-management-inline-back"
                          aria-label="Go back"
                          onClick={() => navigate(-1)}
                        >
                          <FaArrowLeft aria-hidden="true" />
                        </button>
                        <Typography.Title
                          level={2}
                          className="fontFamily1 course-management-page-title"
                        >
                          Resource Library
                        </Typography.Title>
                      </div>
                    </Col>
                    <Col
                      xs={24}
                      md={10}
                      lg={8}
                      className="quiz-management-actions-col"
                    >
                      <div className="course-management-primary-actions">
                        <Button
                          type="primary"
                          shape="round"
                          size="large"
                          className="loginButton"
                          onClick={() => navigate("/asset-management/add")}
                        >
                          Add Asset
                        </Button>
                      </div>
                    </Col>
                  </Row>

                  <Row className="course-management-sub-toolbar" gutter={[16, 16]}>
                    <Col xs={24} md={12}>
                      <div className="course-management-page-size">
                        <span className="course-management-page-size-label">
                          Show
                        </span>
                        <Select
                          size="large"
                          className="chartSelectBox"
                          value={paginationConfig.limit}
                          onChange={handleLimitChange}
                          style={{ width: 70, textAlign: "left" }}
                          options={[
                            { value: 10, label: "10" },
                            { value: 20, label: "20" },
                            { value: 30, label: "30" },
                            { value: 50, label: "50" },
                          ]}
                        />
                        <span className="course-management-page-size-label">
                          entries
                        </span>
                      </div>
                    </Col>
                    <Col
                      xs={24}
                      md={12}
                      className="upcoming-lessons-toolbar-actions"
                    >
                      <Popover
                        content={filterContent}
                        trigger="click"
                        open={filterOpen}
                        onOpenChange={setFilterOpen}
                        placement="bottomRight"
                        arrow={false}
                      >
                        <Button
                          type="primary"
                          shape="circle"
                          className="upcoming-lessons-filter-trigger"
                          aria-label="Open filters"
                        >
                          <FaFilter style={{ fontSize: 16, color: "white" }} />
                        </Button>
                      </Popover>
                      <Input
                        className="mainInput dashInput upcoming-lessons-search"
                        placeholder="Search by title…"
                        value={filter.keyword}
                        onChange={(e) =>
                          setFilter((f) => ({ ...f, keyword: e.target.value }))
                        }
                        onPressEnter={handleSearch}
                        suffix={
                          <FaSearch
                            style={{
                              color: "#64748b",
                              fontSize: 16,
                              cursor: "pointer",
                            }}
                            onClick={handleSearch}
                          />
                        }
                      />
                    </Col>
                  </Row>

                  <Row className="upcoming-lessons-table-shell">
                    {loading ? (
                      <div className="upcoming-lessons-skeleton-wrap">
                        <Skeleton active paragraph={{ rows: 6 }} />
                      </div>
                    ) : (
                      <div className="upcoming-lessons-table-wrap">
                        <Table
                          className="styledTable upcoming-lessons-table"
                          dataSource={assets}
                          columns={columns}
                          rowKey="_id"
                          pagination={false}
                          scroll={{ x: "max-content" }}
                        />
                      </div>
                    )}
                  </Row>

                  <Row className="upcoming-lessons-pagination-row" gutter={[16, 16]}>
                    <Col xs={24} md={12}>
                      <p className="fontFamily1 upcoming-lessons-pagination-meta">
                        {messageRecord}
                      </p>
                    </Col>
                    <Col xs={24} md={12}>
                      <div className="upcoming-lessons-pagination-controls">
                        <Pagination
                          className="styledPagination"
                          onChange={handlePageChange}
                          current={paginationConfig.pageNumber}
                          pageSize={paginationConfig.limit}
                          total={paginationConfig.totalDocs}
                          itemRender={itemRender}
                        />
                      </div>
                    </Col>
                  </Row>
                </div>
              </Card>
            </div>
          </Col>
        </Row>
      </div>
    </Layout>
  );
}

export default AssetManagement;
